import { AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { DevExLoaderComponent } from '../../../devExtreme/src/devExLoader/devExLoader.component';
import { MatLoaderComponent } from '../../../material/src/matLoader/matLoader.component';
import { LoaderStyle } from '../abcComponent.model';
import { AbcComponentService } from '../abcComponent.service';
import { ILoader, LoaderColor, LoaderMode, LoaderPosition } from './abcLoader';

@Component({
    selector: 'abcLoader',
    template: ` <ng-container *ngTemplateOutlet="style !== 2 ? abcLoader : defaultLoader"> </ng-container>
    <ng-template #abcLoader></ng-template>
    <ng-template #defaultLoader>
      <div class="loaderContainer {{ class }}" id="{{ attrId }}" [ngClass]="show ? 'show' : ''">
        <div class="circle"></div>
        <div class="circle"></div>
        <div class="circle"></div>
      </div>
    </ng-template>`,
    styles: [
        `
      .show {
        opacity: 0.8 !important;
        display: flex;
        position: absolute;
        bottom: calc(50% - 15px);
        left: calc(50% - 30px);
        z-index: 1000;
        transition: opacity 0.3s ease-in;
      }

      .circle {
        background-color: var(--PrimaryMainColor) !important;
        width: 10px;
        height: 10px;
        border-radius: 50%;
        margin: 5px;
        animation: bounce 0.4s ease-in infinite;
      }

      .circle:nth-of-type(2) {
        animation-delay: 0.4s;
        opacity: 0.8;
      }

      .circle:nth-of-type(3) {
        animation-delay: 0.2s;
        opacity: 0.6;
      }
      .loaderContainer {
        height: 0px;
        opacity: 0;
      }
      @keyframes bounce {
        0%,
        100% {
          transform: translateY(0);
        }

        50% {
          transform: translateY(-10px);
        }
      }
    `,
    ],
    standalone: false
})
export class AbcLoaderComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input('percentageValue') attrPercentageValue: boolean;
  @Input('id') attrId: string;
  private _width: string;
  private _height: string;
  private _class: string;
  private _visible: boolean;
  private _rtlEnabled: boolean;
  private _delay: string;
  private _color: LoaderColor;
  private _showIndicator: boolean;
  private _text: string;
  private _value: number;
  private _mode: LoaderMode;
  private _hoverStateEnabled: boolean;
  private _message: string;
  private _minHeight: number;
  private _maxHeight: number;
  private _maxWidth: number;
  private _minWidth: number;
  private _shadingColor: string;
  private _shading: boolean;
  private _showPane: boolean;
  private _focusStateEnabled: boolean;
  private _position: LoaderPosition;
  private _show: boolean = false;
  private _style: LoaderStyle;

  @Input()
  get style() {
    return this._style;
  }
  set style(data: LoaderStyle) {
    this._style = data;

    if (this.componentRef) {
      this.viewContainerRef.clear();
      this.createComponentInstance().subscribe((component) => {
        this.eventListener.forEach((element) => {
          component.addEventListener(element.type, element.listener);
        });
      });
    }
  }

  @Input()
  get height() {
    return this._height;
  }
  set height(data: string) {
    this._height = data;
    if (this.componentRef) {
      this.componentRef.height = data;
    }
  }

  @Input()
  get delay() {
    return this._delay;
  }
  set delay(data: string) {
    this._delay = data;
    if (this.componentRef) {
      this.componentRef.delay = data;
    }
  }

  @Input()
  get show() {
    return this._show;
  }
  set show(data: boolean) {
    this._show = data;
    if (this.componentRef) {
      this.componentRef.show = data;
    }
  }

  @Input()
  get mode() {
    return this._mode;
  }
  set mode(data: LoaderMode) {
    this._mode = data;
    if (this.componentRef) {
      this.componentRef.mode = data;
    }
  }

  @Input()
  get minHeight() {
    return this._minHeight;
  }
  set minHeight(data: number) {
    this._minHeight = data;
    if (this.componentRef) {
      this.componentRef.minHeight = data;
    }
  }

  @Input()
  get maxWidth() {
    return this._maxWidth;
  }
  set maxWidth(data: number) {
    this._maxWidth = data;
    if (this.componentRef) {
      this.componentRef.maxWidth = data;
    }
  }

  @Input()
  get minWidth() {
    return this._minWidth;
  }
  set minWidth(data: number) {
    this._minWidth = data;
    if (this.componentRef) {
      this.componentRef.minWidth = data;
    }
  }

  @Input()
  get shadingColor() {
    return this._shadingColor;
  }
  set shadingColor(data: string) {
    this._shadingColor = data;
    if (this.componentRef) {
      this.componentRef.shadingColor = data;
    }
  }

  @Input()
  get maxHeight() {
    return this._maxHeight;
  }
  set maxHeight(data: number) {
    this._maxHeight = data;
    if (this.componentRef) {
      this.componentRef.maxHeight = data;
    }
  }

  @Input()
  get message() {
    return this._message;
  }
  set message(data: string) {
    this._message = data;
    if (this.componentRef) {
      this.componentRef.message = data;
    }
  }

  @Input()
  get hoverStateEnabled() {
    return this._hoverStateEnabled;
  }
  set hoverStateEnabled(data: boolean) {
    this._hoverStateEnabled = data;
    if (this.componentRef) {
      this.componentRef.hoverStateEnabled = data;
    }
  }

  @Input()
  get focusStateEnabled() {
    return this._focusStateEnabled;
  }
  set focusStateEnabled(data: boolean) {
    this._focusStateEnabled = data;
    if (this.componentRef) {
      this.componentRef.focusStateEnabled = data;
    }
  }
  @Input()
  get shading() {
    return this._shading;
  }
  set shading(data: boolean) {
    this._shading = data;
    if (this.componentRef) {
      this.componentRef.shading = data;
    }
  }

  @Input()
  get showPane() {
    return this._showPane;
  }
  set showPane(data: boolean) {
    this._showPane = data;
    if (this.componentRef) {
      this.componentRef.showPane = data;
    }
  }

  @Input()
  get position() {
    return this._position;
  }
  set position(data: LoaderPosition) {
    this._position = data;
    if (this.componentRef) {
      this.componentRef.position = data;
    }
  }

  @Input()
  get color() {
    return this._color;
  }
  set color(data: LoaderColor) {
    this._color = data;
    if (this.componentRef) {
      this.componentRef.color = data;
    }
  }

  @Input()
  get text() {
    return this._text;
  }
  set text(data: string) {
    this._text = data;
    if (this.componentRef) {
      this.componentRef.text = data;
    }
  }

  @Input()
  get showIndicator() {
    return this._showIndicator;
  }
  set showIndicator(data: boolean) {
    this._showIndicator = data;
    if (this.componentRef) {
      this.componentRef.showIndicator = data;
    }
  }

  @Input()
  get width() {
    return this._width;
  }
  set width(data: string) {
    this._width = data;
    if (this.componentRef) {
      this.componentRef.width = data;
    }
  }

  @Input()
  get value() {
    return this._value;
  }
  set value(data: number) {
    this._value = data;
    if (this.componentRef) {
      this.componentRef.value = data;
    }
  }

  @Input()
  get rtlEnabled() {
    return this._rtlEnabled;
  }
  set rtlEnabled(data: boolean) {
    this._rtlEnabled = data;
    if (this.componentRef) {
      this.componentRef.rtlEnabled = data;
    }
  }

  @Input()
  get visible() {
    return this._visible;
  }
  set visible(data: boolean) {
    this._visible = data;
    if (this.componentRef) {
      this.componentRef.visible = data;
    }
  }

  @Input()
  get class() {
    return this._class;
  }
  set class(data: string) {
    this._class = data;
    if (this.componentRef) {
      this.componentRef.class = data;
    }
  }
  @ViewChild('abcLoader', { static: true, read: ViewContainerRef }) viewContainerRef: ViewContainerRef;
  htmlAttributes: ILoader;
  eventListener = [];
  private componentRef;
  private subscription: Subscription;
  get id(): string {
    return this.componentRef?.id;
  }
  constructor(private abcComponentService: AbcComponentService) {
    this.style = LoaderStyle.default;
  }

  ngOnInit() {
    this.setLoaderAttributesIfAvailable();
    this.subscription = this.createComponentInstance().subscribe((component) => {
      this.eventListener.forEach((element) => {
        component.addEventListener(element.type, element.listener);
      });
    });
  }

  hideLoader() {
    this.show = false;
  }

  showLoader() {
    if (this.componentRef) {
      this.show = true;
    }
  }

  ngAfterViewInit() {
    this.abcComponentService.addInstance(this);
  }

  ngOnDestroy(): void {
    this.eventListener = [];
    this.abcComponentService.removeInstance(this);
    this.subscription.unsubscribe();
  }

  createComponentInstance(): Observable<any> {
    const observable = new Observable((observer) => {
      switch (this.style) {
        case LoaderStyle.Material: {
          this.componentRef = this.abcComponentService.loadComponent(
            MatLoaderComponent,
            this.viewContainerRef
          ).componentRef.instance;
          if (this.htmlAttributes.id) this.componentRef.id = this.htmlAttributes.id;
          if (this.htmlAttributes.color) this.componentRef.color = this.htmlAttributes.color;
          if (this.htmlAttributes.mode) this.componentRef.mode = this.htmlAttributes.mode;
          if (this.htmlAttributes.percentageValue)
            this.componentRef.percentageValue = this.htmlAttributes.percentageValue;
          if (this.htmlAttributes.value) this.componentRef.value = this.htmlAttributes.value;
          if (this.htmlAttributes.text) this.componentRef.text = this.htmlAttributes.text;
          if (this.htmlAttributes.class) this.componentRef.class = this.htmlAttributes.class;
          if (this.text) this.componentRef.text = this.text;
          if (this.class) this.componentRef.class = this.class;
          this.componentRef.show = this.show;
          break;
        }
        case LoaderStyle.DevExpress: {
          this.componentRef = this.abcComponentService.loadComponent(
            DevExLoaderComponent,
            this.viewContainerRef
          ).componentRef.instance;
          if (this.htmlAttributes.id) this.componentRef.id = this.htmlAttributes.id;
          if (this.htmlAttributes.delay) this.componentRef.delay = this.htmlAttributes.delay;
          if (this.htmlAttributes.showIndicator) this.componentRef.showIndicator = this.htmlAttributes.showIndicator;
          if (this.htmlAttributes.showPane) this.componentRef.showPane = this.htmlAttributes.showPane;
          if (this.htmlAttributes.visible) this.componentRef.visible = this.htmlAttributes.visible;
          if (this.htmlAttributes.rtlEnabled) this.componentRef.rtlEnabled = this.htmlAttributes.rtlEnabled;
          if (this.htmlAttributes.hoverStateEnabled)
            this.componentRef.hoverStateEnabled = this.htmlAttributes.hoverStateEnabled;
          if (this.htmlAttributes.message) this.componentRef.message = this.htmlAttributes.message;
          if (this.htmlAttributes.minHeight) this.componentRef.minHeight = this.htmlAttributes.minHeight;
          if (this.htmlAttributes.maxHeight) this.componentRef.maxHeight = this.htmlAttributes.maxHeight;
          if (this.htmlAttributes.maxWidth) this.componentRef.maxWidth = this.htmlAttributes.maxWidth;
          if (this.htmlAttributes.minWidth) this.componentRef.minWidth = this.htmlAttributes.minWidth;
          if (this.htmlAttributes.shadingColor) this.componentRef.shadingColor = this.htmlAttributes.shadingColor;
          if (this.htmlAttributes.shading) this.componentRef.shading = this.htmlAttributes.shading;
          if (this.htmlAttributes.delay) this.componentRef.delay = this.htmlAttributes.delay;
          if (this.htmlAttributes.position) this.componentRef.position = this.htmlAttributes.position;
          if (this.htmlAttributes.focusStateEnabled)
            this.componentRef.focusStateEnabled = this.htmlAttributes.focusStateEnabled;
          if (this.htmlAttributes.class) this.componentRef.class = this.htmlAttributes.class;
          this.componentRef.show = this.show;
          break;
        }
      }
      observer.next(this.componentRef);
    });

    return observable;
  }

  setLoaderAttributesIfAvailable() {
    if (this.attrId) {
      this.htmlAttributes = {
        id: this.attrId,
        height: this.height,
        width: this.width,
        color: this.color,
        mode: this.mode,
        percentageValue: this.attrPercentageValue,
        value: this.value,
        position: this.position,
        visible: (this.visible = true),
        rtlEnabled: this.rtlEnabled,
        delay: this.delay,
        shading: this.shading,
        showPane: this.showPane,
        class: this.class,
        showIndicator: (this.showIndicator = true),
        hoverStateEnabled: this.hoverStateEnabled,
        focusStateEnabled: this.focusStateEnabled,
        message: this.message,
        minHeight: this.minHeight,
        maxHeight: this.maxHeight,
        maxWidth: this.maxWidth,
        minWidth: this.minWidth,
        shadingColor: this.shadingColor,
      };
    }
  }
}
